/**
 * Código de barras Code 128, em SVG.
 *
 * Por que Code 128 e não EAN-13, apesar de os códigos do sistema começarem com
 * 789 e terem 13 dígitos: eles são gerados aleatoriamente (ver o cadastro de
 * produto) e NÃO carregam dígito verificador válido de EAN. Desenhá-los como
 * EAN-13 produziria um símbolo que o leitor calcula, vê o verificador errado e
 * recusa — a etiqueta sairia bonita e não bipava. Code 128 não impõe estrutura
 * de verificador ao conteúdo, então o que está no cadastro é o que o leitor
 * devolve, que é exatamente o que o PDV procura.
 *
 * Sem dependência externa de propósito: é ~100 linhas de tabela e um laço, e o
 * agente de impressão bloqueia qualquer requisição a CDN.
 */

/* Larguras de barra/espaço de cada símbolo (0–106). Índice 106 é o STOP. */
const PATTERNS = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213",
  "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132",
  "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211",
  "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331",
  "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111",
  "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214",
  "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141",
  "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141",
  "114131", "311141", "411131", "211412", "211214", "211232", "2331112",
];

const START_B = 104;
const START_C = 105;
// Só existe a troca B→C: um código numérico ímpar começa em B (um dígito) e
// segue em C até o fim. Voltar para B no meio nunca é necessário aqui, porque a
// escolha do subconjunto é feita uma vez, olhando o texto inteiro.
const CODE_C = 99;
const STOP = 106;

const soDigitos = (s) => /^\d+$/.test(s);

/**
 * Converte o texto na sequência de símbolos.
 *
 * Usa o subconjunto C (dois dígitos por símbolo) quando o conteúdo é numérico —
 * é o que faz um código de 13 dígitos caber numa etiqueta de 60mm com módulo
 * legível. Só em B, o mesmo código fica ~45% mais largo e o leitor começa a
 * errar quando a impressora térmica engrossa a barra.
 */
export function encode(texto) {
  const valores = [];
  let i = 0;

  if (soDigitos(texto)) {
    // Comprimento ímpar: o primeiro dígito vai em B e o resto, aos pares, em C.
    if (texto.length % 2 === 1) {
      valores.push(START_B, texto.charCodeAt(0) - 32, CODE_C);
      i = 1;
    } else {
      valores.push(START_C);
    }
    for (; i < texto.length; i += 2) valores.push(Number(texto.substr(i, 2)));
  } else {
    valores.push(START_B);
    for (const ch of texto) {
      const c = ch.charCodeAt(0);
      // Fora de 32–126 o subconjunto B não representa: vira espaço, para a
      // etiqueta sair legível em vez de o símbolo ficar inválido.
      valores.push(c >= 32 && c <= 126 ? c - 32 : 0);
    }
  }

  // Checksum: o valor de início mais cada símbolo pela posição, módulo 103.
  const check = valores.reduce((soma, v, pos) => soma + v * (pos === 0 ? 1 : pos), 0) % 103;
  valores.push(check, STOP);

  return valores;
}

/**
 * SVG do código de barras, pronto para embutir no HTML da etiqueta.
 *
 * `moduleWidth` é a largura da barra mais fina, em milímetros. 0,25mm é o piso
 * prático para leitor de balcão em impressora térmica; abaixo disso a barra
 * "sangra" no papel e o código para de ler.
 */
export function code128Svg(texto, { heightMm = 12, moduleWidth = 0.3 } = {}) {
  const valor = String(texto || "").trim();
  if (!valor) return "";

  const larguras = encode(valor).map((v) => PATTERNS[v]).join("");
  let x = 0;
  let barras = "";

  // Dígitos alternam barra/espaço, começando por barra. O valor de cada um é
  // quantos módulos ele ocupa.
  for (let i = 0; i < larguras.length; i += 1) {
    const modulos = Number(larguras[i]);
    if (i % 2 === 0) {
      barras += `<rect x="${(x * moduleWidth).toFixed(3)}" y="0" width="${(modulos * moduleWidth).toFixed(3)}" height="${heightMm}" fill="#000"/>`;
    }
    x += modulos;
  }

  const larguraMm = x * moduleWidth;

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${larguraMm.toFixed(2)}mm" height="${heightMm}mm" `
    + `viewBox="0 0 ${larguraMm.toFixed(2)} ${heightMm}" preserveAspectRatio="xMidYMid meet" role="img" `
    + `aria-label="Código de barras ${valor}">${barras}</svg>`;
}

/** Largura do símbolo em mm — usada para escolher o módulo que cabe na etiqueta. */
export function code128WidthMm(texto, moduleWidth = 0.3) {
  const valor = String(texto || "").trim();
  if (!valor) return 0;
  const modulos = encode(valor)
    .map((v) => PATTERNS[v])
    .join("")
    .split("")
    .reduce((s, d) => s + Number(d), 0);

  return modulos * moduleWidth;
}
